// Typed view of the `*.style.json` tunables.
//
// The JSON files are the single source of truth for every render tunable —
// hand-edited, hot-reloaded by Vite, and written back by the material lab's
// Save button. This module gives them types and a fail-fast load: a key in
// the JSON that the type doesn't know about (or vice versa) should explode
// at startup, not silently no-op a slider.
//
// Colors are hex strings ("#2de2ff") in JSON — Tweakpane-native, and
// converted to THREE.Color exactly once at material build.

import { checkStyleKeys, createStyleListeners, deepAssign } from "@neoweb/devtools";
import bloomJson from "./bloom.style.json";
import boardsJson from "./boards.style.json";
import cameraJson from "./camera.style.json";
import entitiesJson from "./entities.style.json";
import iceJson from "./ice.style.json";
import spaceJson from "./space.style.json";

export type LineFamilyStyle = {
  color: string;
  coreWidth: number;
  coreIntensity: number;
  glowWidth: number;
  glowIntensity: number;
};

export type IceStyle = {
  baseColor: string;
  lineSoftness: number;
  center: LineFamilyStyle;
  blue: LineFamilyStyle;
  goalLine: LineFamilyStyle;
  circles: { ringWidth: number; coreIntensity: number; glowWidth: number; glowIntensity: number };
  dots: { coreIntensity: number; haloIntensity: number };
  crease: { fillIntensity: number; ringIntensity: number };
  sparkleDensity: number;
  sparkleIntensity: number;
  sheenIntensity: number;
  reflectIntensity: number;
  reflectFresnelPower: number;
};

export type BoardsStyle = {
  boardHeight: number;
  glass: {
    seeThrough: number;
    refractStrength: number;
    fresnelPower: number;
    rimIntensity: number;
    tintStrength: number;
  };
  trim: {
    railRadius: number;
    leftColor: string;
    rightColor: string;
    intensity: number;
    baseIntensity: number;
    pulseFalloff: number;
    pulseDecay: number;
    pulseBoost: number;
  };
  goal: {
    homeColor: string;
    awayColor: string;
    frameRadius: number;
    frameIntensity: number;
    fillIntensity: number;
    flashBoost: number;
    flashDecay: number;
  };
};

export type BloomStyle = {
  strength: number;
  radius: number;
  threshold: number;
  exposure: number;
};

export type SpaceStyle = {
  starCount: number;
  shellRadius: number;
  starSize: number;
  brightness: number;
  twinkleAmount: number;
  twinkleRate: number;
};

export type EntitiesStyle = {
  skater: {
    radiusFactor: number;
    domeSquash: number;
    domeAlpha: number;
    domeIntensity: number;
    rimPower: number;
    ringInnerFrac: number;
    ringIntensity: number;
    finLengthFrac: number;
    finIntensity: number;
    nodeIntensity: number;
    tetherIntensity: number;
  };
  trail: { seconds: number; minSpeed: number; maxAlpha: number; intensity: number };
  indicators: {
    selectionColor: string;
    selectionIntensity: number;
    protectColor: string;
    protectPulseHz: number;
    protectIntensity: number;
    chargeStartColor: string;
    chargeEndColor: string;
    chargeIntensity: number;
    stunColor: string;
    stunIntensity: number;
  };
  goalie: {
    bodyRadiusFrac: number;
    domeIntensity: number;
    shieldIntensity: number;
    saveRingColor: string;
    saveRingIntensity: number;
    saveRingAlphaReady: number;
    saveRingAlphaSaving: number;
    saveRingAlphaRecovering: number;
    pulledDim: number;
  };
  puck: {
    coreColor: string;
    ringColor: string;
    coreIntensity: number;
    ringIntensity: number;
    lightColor: string;
    lightIntensity: number;
    lightDistance: number;
    streakColor: string;
    streakIntensity: number;
    streakMinSpeed: number;
    liftMax: number;
    shadowAlpha: number;
  };
};

export type CameraStyle = {
  follow: { pitchDeg: number; fovDeg: number; distance: number };
  overview: {
    position: { x: number; y: number; z: number };
    lookAt: { x: number; y: number; z: number };
    fovDeg: number;
  };
};

export type StyleName = "ice" | "boards" | "bloom" | "space" | "camera" | "entities";

// The imported JSON IS the canonical value; checkStyleKeys compares it against
// itself only on shape (a no-op for the static import, but the same check
// runs against hot-updated and lab-posted payloads via validateStylePayload).
export const iceStyle: IceStyle = iceJson;
export const boardsStyle: BoardsStyle = boardsJson;
export const bloomStyle: BloomStyle = bloomJson;
export const spaceStyle: SpaceStyle = spaceJson;
export const cameraStyle: CameraStyle = cameraJson;
export const entitiesStyle: EntitiesStyle = entitiesJson;

const references: Record<StyleName, unknown> = {
  ice: iceJson,
  boards: boardsJson,
  bloom: bloomJson,
  space: spaceJson,
  camera: cameraJson,
  entities: entitiesJson,
};

/** Validate an incoming style payload (lab Save, HMR) against the known shape. */
export function validateStylePayload(name: StyleName, payload: unknown): void {
  checkStyleKeys(name, payload, references[name]);
}

// --- Hot updates ----------------------------------------------------------
// This module is the JSONs' direct importer, so the HMR accept handlers
// live here: a hot payload is validated, written into the live (mutable)
// exported style object, and subscribers (lab pane, views) are notified to
// push the new values into uniforms. Dev-only; tree-shaken in production.

const styleListeners = createStyleListeners<StyleName>();

/** Subscribe to hot style-JSON updates. Returns an unsubscribe. */
export const onStyleChange = styleListeners.onChange;

if (import.meta.hot) {
  const targets: Record<StyleName, Record<string, unknown>> = {
    ice: iceStyle,
    boards: boardsStyle,
    bloom: bloomStyle,
    space: spaceStyle,
    camera: cameraStyle,
    entities: entitiesStyle,
  };
  // Vite's HMR analysis needs literal dep strings in each accept() call —
  // a helper taking the path as a variable silently degrades to a module
  // self-accept (full re-execution, listeners lost).
  const handle =
    (name: StyleName) =>
    (m: unknown): void => {
      const payload = (m as { default?: unknown } | undefined)?.default;
      if (payload === undefined) return;
      try {
        validateStylePayload(name, payload);
      } catch (err) {
        console.error(`[style] rejected hot payload for ${name}:`, err);
        return;
      }
      deepAssign(targets[name], payload as Record<string, unknown>);
      styleListeners.notify(name);
    };
  import.meta.hot.accept("./ice.style.json", handle("ice"));
  import.meta.hot.accept("./boards.style.json", handle("boards"));
  import.meta.hot.accept("./bloom.style.json", handle("bloom"));
  import.meta.hot.accept("./space.style.json", handle("space"));
  import.meta.hot.accept("./camera.style.json", handle("camera"));
  import.meta.hot.accept("./entities.style.json", handle("entities"));
}
